import { PropsWithChildren } from 'react'
import { IdeyaRoute } from './router.def.ts'
import { useAppSelector } from '../redux/hooks.ts'
import { selectUser } from '../features/user/user.slice.ts'
import { intersection } from 'lodash'
import { FIRM_ROLE, INTERNAL_ROLE } from './roles.ts'
import { useNavigate } from 'react-router-dom'

interface RouteGuardProps extends PropsWithChildren {
  route: IdeyaRoute
}

export const RouteGuard: React.FC<RouteGuardProps> = (props) => {
  const { route, children } = props
  const navigate = useNavigate()
  const user = useAppSelector(selectUser)
  const {
    allowedFirmRoles = [],
    allowedGroupRoles = [],
    deniedFirmRoles = [],
    deniedGroupRoles = [],
    allowedInternalRoles = [],
    globalCanAccess = true,
  } = route

  if (!user) {
    navigate('/login')
    return <></>
  }

  const { firmRole, groupRole, role, region } = user

  if (!globalCanAccess && region === 'Global')
    return <div>Not allowed access page</div>

  if (
    allowedFirmRoles.length === 0 &&
    allowedGroupRoles.length === 0 &&
    allowedInternalRoles.length === 0
  ) {
    return <>{children}</>
  }

  const matchedAllowedFirmRoles = intersection(allowedFirmRoles, [firmRole])
  const matchedAllowedGroupRoles = intersection(allowedGroupRoles, [groupRole])
  const matchedDeniedFirmRoles = intersection(deniedFirmRoles, [firmRole])
  const matchedDeniedGroupRoles = intersection(deniedGroupRoles, [groupRole])
  const matchedAllowedInternalRoles = intersection(allowedInternalRoles, [role])

  const hasAllowedFirmRole = matchedAllowedFirmRoles.length > 0
  const hasAllowedGroupRole = matchedAllowedGroupRoles.length > 0
  const hasDeniedFirmRole = matchedDeniedFirmRoles.length > 0
  const hasDeniedGroupRole = matchedDeniedGroupRoles.length > 0
  const hasAllowedInternalRole = matchedAllowedInternalRoles.length > 0
  const isComplianceRole =
    firmRole === FIRM_ROLE.ROLE_COMPLIANCE &&
    allowedFirmRoles.includes(FIRM_ROLE.ROLE_COMPLIANCE)
  const isInternalMasterOrAdmin =
    role === INTERNAL_ROLE.ROLE_MASTER ||
    role === INTERNAL_ROLE.ROLE_ADMIN ||
    role === INTERNAL_ROLE.ROLE_VIEWONLY

  if (
    (hasAllowedFirmRole &&
      (hasAllowedGroupRole || isComplianceRole) &&
      !hasDeniedGroupRole) ||
    (hasAllowedInternalRole && !hasDeniedFirmRole && !hasDeniedGroupRole) ||
    isInternalMasterOrAdmin
  ) {
    return <>{children}</>
  }

  return <div>Not allowed access page</div>
}
